import { API_BASE_URL } from '@/lib/api';

type WebMcpTool = {
    name: string;
    description: string;
    inputSchema: Record<string, unknown>;
    execute: (input: Record<string, unknown>) => Promise<unknown>;
};

type WebMcpNavigator = Navigator & {
    modelContext?: {
        provideContext?: (context: { tools: WebMcpTool[] }) => void | Promise<void>;
    };
};

const SITE_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');

async function apiGet(path: string, params?: Record<string, string | number | undefined>) {
    const url = new URL(`${API_BASE_URL}${path}`);
    if (params) {
        for (const [k, v] of Object.entries(params)) {
            if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
        }
    }
    const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
    return res.json();
}

async function apiPost(path: string, body: unknown) {
    const res = await fetch(`${API_BASE_URL}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`POST ${path} failed: ${res.status}`);
    return res.json();
}

async function fetchText(absoluteUrl: string) {
    const res = await fetch(absoluteUrl, { headers: { Accept: 'text/plain, text/markdown' } });
    if (!res.ok) throw new Error(`GET ${absoluteUrl} failed: ${res.status}`);
    return res.text();
}

const langSchema = { type: 'string', enum: ['ar', 'en'], description: 'Language code (defaults to ar)' };

const tools: WebMcpTool[] = [
    {
        name: 'get_site_knowledge',
        description:
            'Return the TRBD site knowledge bundle as markdown (company profile, services, flagships, latest blog posts, FAQ, contact channels). Use this as the primary grounding source for general questions about TRBD.',
        inputSchema: {
            type: 'object',
            properties: {
                lang: langSchema,
                deep: {
                    type: 'boolean',
                    description: 'If true, include full blog post bodies (larger payload). Default false.',
                },
            },
            additionalProperties: false,
        },
        execute: async ({ lang, deep }) => {
            const langQ = lang === 'en' ? 'en' : 'ar';
            const path = deep ? '/llms-full.txt' : '/llms.txt';
            return fetchText(`${SITE_ORIGIN}${path}?lang=${langQ}`);
        },
    },
    {
        name: 'list_blog_posts',
        description: 'List published TRBD blog posts with pagination, category, tag, or search filters.',
        inputSchema: {
            type: 'object',
            properties: {
                lang: langSchema,
                page: { type: 'integer', minimum: 1, description: 'Page number (default 1)' },
                limit: { type: 'integer', minimum: 1, maximum: 50, description: 'Posts per page (default 12)' },
                category: { type: 'string', description: 'Category slug' },
                tag: { type: 'string' },
                search: { type: 'string', description: 'Full-text search query' },
                featured: { type: 'boolean' },
            },
            additionalProperties: false,
        },
        execute: ({ lang, page, limit, category, tag, search, featured }) =>
            apiGet('/blog/posts', {
                lang: (lang as string) || 'ar',
                page: page as number | undefined,
                limit: limit as number | undefined,
                category: category as string | undefined,
                tag: tag as string | undefined,
                search: search as string | undefined,
                featured: featured === true ? 'true' : undefined,
            }),
    },
    {
        name: 'get_blog_post',
        description: 'Fetch a single TRBD blog post by slug, including full body content.',
        inputSchema: {
            type: 'object',
            properties: {
                slug: { type: 'string', description: 'Post slug (from the URL)' },
                lang: langSchema,
            },
            required: ['slug'],
            additionalProperties: false,
        },
        execute: ({ slug, lang }) =>
            apiGet(`/blog/posts/${encodeURIComponent(String(slug))}`, { lang: (lang as string) || 'ar' }),
    },
    {
        name: 'list_blog_categories',
        description: 'List blog categories with post counts.',
        inputSchema: {
            type: 'object',
            properties: { lang: langSchema },
            additionalProperties: false,
        },
        execute: ({ lang }) => apiGet('/blog/categories', { lang: (lang as string) || 'ar' }),
    },
    {
        name: 'list_blog_tags',
        description: 'List all blog tags used across published posts.',
        inputSchema: { type: 'object', properties: {}, additionalProperties: false },
        execute: () => apiGet('/blog/tags'),
    },
    {
        name: 'submit_contact_request',
        description:
            'Submit a new contact / project request to TRBD sales. All four fields (name, email, service, message) are required.',
        inputSchema: {
            type: 'object',
            properties: {
                name: { type: 'string', description: 'Full name of the person' },
                email: { type: 'string', format: 'email' },
                service: {
                    type: 'string',
                    description: 'Service of interest (e.g., web development, mobile app, branding, marketing).',
                },
                message: { type: 'string', description: 'Description of the project / request' },
            },
            required: ['name', 'email', 'service', 'message'],
            additionalProperties: false,
        },
        execute: (input) => apiPost('/contact', input),
    },
];

export function registerWebMcp(): void {
    if (typeof navigator === 'undefined') return;
    const nav = navigator as WebMcpNavigator;
    const provideContext = nav.modelContext?.provideContext;
    if (typeof provideContext !== 'function') return;
    try {
        void provideContext.call(nav.modelContext, { tools });
    } catch (err) {
        console.warn('[webmcp] provideContext failed', err);
    }
}

registerWebMcp();
